/**
 * La campagna d'arrivo: le `utm_*` dell'indirizzo con cui la visita e' iniziata.
 *
 * ⚠️ `catturaCampagna()` va chiamata IN CIMA a `main.tsx`, fuori da ogni
 * componente e da ogni effetto. Non e' pignoleria: l'app RISCRIVE il proprio
 * indirizzo al primo effetto — `applyDeepLink` fa `replaceState(…, pathname)`
 * per la landing, e subito dopo l'effetto della lingua porta `/` su `/it/` —
 * quindi dopo il mount la coda `?utm_source=…` non esiste piu'. Lo script
 * Umami, che e' `defer` e arriva dalla rete, fotografa `location.href` ancora
 * piu' tardi: se aspettassimo lui, la campagna sarebbe sempre persa. Chi
 * domani spostasse questa chiamata dentro un `useEffect` romperebbe la misura
 * in silenzio, senza che un test fallisca da solo.
 *
 * Il risultato resta in memoria e basta: NIENTE `localStorage` o
 * `sessionStorage`. Scrivere sul terminale prima che il banner abbia risposto
 * e' proprio cio' che l'art. 5(3) ePrivacy copre, e qui non serve — la
 * campagna interessa solo per la durata di questa visita. Se la persona
 * ricarica prima di accettare, l'attribuzione si perde: e' il prezzo giusto.
 *
 * Whitelist stretta: si leggono i cinque parametri canonici e nient'altro.
 * L'indirizzo d'arrivo puo' portare gettoni segreti (`?challenge=…`,
 * `?tournament=…&t=…`) che non devono finire in un evento di misura piu' di
 * quanto debbano finire in una visita (vedi `data-exclude-search` in
 * `load-umami.ts`).
 */

const CHIAVI = {
  utm_source: 'source',
  utm_medium: 'medium',
  utm_campaign: 'campaign',
  utm_content: 'content',
  utm_term: 'term',
} as const;

/** Oltre questo, un valore e' rumore: si tronca invece di buttarlo. */
const MAX_LUNGHEZZA = 64;

export type Campagna = Partial<Record<(typeof CHIAVI)[keyof typeof CHIAVI], string>>;

let campagna: Campagna | null = null;

/**
 * Legge le `utm_*` dalla stringa di ricerca d'arrivo. Idempotente nel senso che
 * conta: una seconda chiamata con una stringa VUOTA non cancella quello che la
 * prima aveva trovato — l'app si riscrive l'indirizzo da sola, e una cattura
 * tardiva non deve poter azzerare la campagna vera.
 */
export function catturaCampagna(search: string = window.location.search): void {
  const trovata: Campagna = {};
  let qualcosa = false;
  try {
    const params = new URLSearchParams(search);
    for (const [chiave, nome] of Object.entries(CHIAVI)) {
      const grezzo = params.get(chiave);
      if (grezzo === null) continue;
      const valore = grezzo.trim().slice(0, MAX_LUNGHEZZA);
      if (!valore) continue;
      trovata[nome] = valore;
      qualcosa = true;
    }
  } catch {
    /* Una stringa di ricerca malformata non deve impedire l'avvio dell'app. */
    return;
  }
  if (qualcosa) campagna = trovata;
}

/** Le `utm_*` di questa visita, o `null` se non si arriva da una campagna. */
export function getCampagna(): Campagna | null {
  return campagna;
}

/** Solo per i test: riporta il modulo allo stato di partenza. */
export function azzeraCampagna(): void {
  campagna = null;
}
